import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { IBuild } from '@/entities/build.entity';
import { formatPanelBuildLabel, resolveBuildLabel } from '@/lib/format-build-label';
import { cn } from '@/lib/utils';
import Pressable from '@/components/motion/pressable';
import { resolveBuildDisplayIconUrl } from '@crave/config/apk-hiding';
import { BoxIcon, ChevronRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

interface SenderBuildPickerProps {
	builds: IBuild[];
	buildId: string;
	onBuildIdChange: (id: string) => void;
	disabled?: boolean;
	label?: string;
	dialogTitle?: string;
	emptyMessage?: string;
	placeholder?: string;
}

function BuildIconThumb({ build, className }: { build: IBuild; className?: string }) {
	const iconUrl = resolveBuildDisplayIconUrl(build);
	const title = build.appName || build.id;
	return (
		<div
			className={cn(
				'relative shrink-0 overflow-hidden rounded-xl border border-border bg-muted/40',
				className,
			)}
		>
			{iconUrl ? (
				<img src={iconUrl} alt={title} className='size-full object-cover' />
			) : (
				<div className='flex size-full items-center justify-center text-muted-foreground'>
					<BoxIcon size={20} />
				</div>
			)}
		</div>
	);
}

export default function SenderBuildPicker({
	builds,
	buildId,
	onBuildIdChange,
	disabled = false,
	label = 'Билд',
	dialogTitle = 'Билд',
	emptyMessage = 'Нет готовых билдов с APK',
	placeholder = 'Выберите билд',
}: SenderBuildPickerProps) {
	const [open, setOpen] = useState(false);
	const selected = useMemo(() => builds.find(b => b.id === buildId), [builds, buildId]);
	const previewLabel = selected
		? formatPanelBuildLabel(selected)
		: buildId
			? resolveBuildLabel(buildId, builds)
			: null;

	function select(id: string) {
		onBuildIdChange(id);
		setOpen(false);
	}

	if (builds.length === 0) {
		return (
			<div className='flex flex-col gap-1.5'>
				<span className='text-sm font-medium leading-none'>{label}</span>
				<p className='text-sm text-muted-foreground'>{emptyMessage}</p>
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-1.5'>
			<span className='text-sm font-medium leading-none'>{label}</span>
			<button
				type='button'
				disabled={disabled}
				onClick={() => setOpen(true)}
				className={cn(
					'flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2 text-left text-[15px] disabled:opacity-50',
					buildId ? 'border-border' : 'border-dashed border-border',
				)}
			>
				{selected ? (
					<BuildIconThumb build={selected} className='size-10' />
				) : (
					<div className='flex size-10 shrink-0 items-center justify-center rounded-xl border border-border bg-muted/40 text-muted-foreground'>
						<BoxIcon size={20} />
					</div>
				)}
				<span className={cn('min-w-0 flex-1 truncate', !previewLabel && 'text-muted-foreground')}>
					{previewLabel ?? placeholder}
				</span>
				<ChevronRightIcon className='size-4 shrink-0 text-muted-foreground' aria-hidden />
			</button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{dialogTitle}</DialogTitle>
					</DialogHeader>
					<ul className='flex max-h-[min(60vh,24rem)] flex-col gap-2 overflow-y-auto overscroll-y-contain touch-scroll'>
						{builds.map(build => {
							const isSelected = build.id === buildId;
							return (
								<li key={build.id}>
									<Pressable
										role='button'
										tabIndex={0}
										onClick={() => select(build.id)}
										className={cn(
											'flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2 text-left active:bg-accent/30',
											isSelected ? 'border-primary bg-primary/5' : 'border-border/60 bg-card/40',
										)}
									>
										<BuildIconThumb build={build} className='size-10' />
										<span className='min-w-0 flex-1 text-[15px]'>{formatPanelBuildLabel(build)}</span>
										<span
											className={cn(
												'size-4 shrink-0 rounded-full border-2',
												isSelected ? 'border-primary bg-primary' : 'border-muted-foreground',
											)}
											aria-hidden
										/>
									</Pressable>
								</li>
							);
						})}
					</ul>
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setOpen(false)}>
							Закрыть
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
